import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Language } from '../types';
import { TRANSLATIONS, SERVICES } from '../data/translations';
import { getSiteConfig } from '../data/siteConfig';
import { getHeroImageSeo, getEquipmentBannerImageSeo, getServiceImageSeo } from '../utils/imageSeo';
import { Phone, Shield, Clock, Wrench, Flame, ShieldAlert, CheckCircle2, ArrowRight, MapPin, Award, ShieldCheck, Star, Wind } from 'lucide-react';
import { TestimonialsCarousel } from './TestimonialsCarousel';
import { FaqSection } from './FaqSection';
import defaultHeroBg from '../assets/images/home_hero_boiler_1789081283486.webp';
import defaultHeroBgMobile from '../assets/images/home_hero_boiler_mobile.webp';
import defaultHeroBgMobileAvif from '../assets/images/home_hero_boiler_mobile.avif';

const InternalLinkingSection = lazy(() => import('./InternalLinkingSection').then(m => ({ default: m.InternalLinkingSection })));

interface HomeViewProps {
  currentLang: Language;
  setView: (view: string) => void;
  onOpenSchema: () => void;
}

export const HomeView: React.FC<HomeViewProps> = ({ currentLang, setView }) => {
  const t = TRANSLATIONS[currentLang];
  const [siteConfig, setSiteConfig] = useState(getSiteConfig());

  useEffect(() => {
    const handleUpdate = () => setSiteConfig(getSiteConfig());
    window.addEventListener('siteConfigUpdated', handleUpdate);
    return () => window.removeEventListener('siteConfigUpdated', handleUpdate);
  }, []);

  const serviceImages = siteConfig.serviceImages;

  return (
    <div className="space-y-16 pb-20">
      {/* Hero Section */}
      <section className="relative text-white overflow-hidden py-16 sm:py-20 lg:py-28 min-h-[580px] sm:min-h-[620px] flex items-center">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-blue-950/80 z-10"></div>
          <picture className="w-full h-full">
            <source media="(max-width: 640px)" srcSet={defaultHeroBgMobileAvif} type="image/avif" />
            <source media="(max-width: 640px)" srcSet={defaultHeroBgMobile} type="image/webp" />
            <source srcSet={siteConfig.heroImage || defaultHeroBg} type="image/webp" />
            <img
              src={siteConfig.heroImage || defaultHeroBg}
              alt={getHeroImageSeo(currentLang).alt}
              title={getHeroImageSeo(currentLang).title}
              width="640"
              height="480"
              className="w-full h-full object-cover object-center"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              referrerPolicy="no-referrer"
            />
          </picture>
        </div>
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:16px_16px] z-20 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-30">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 px-3.5 py-1.5 rounded-full text-blue-200 text-xs font-semibold tracking-wide">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                <span>{currentLang === 'fr' ? 'Intervention 24/7 partout en Belgique • API HEAT (ELF DISTRICT)' : currentLang === 'nl' ? 'Interventie 24/7 in heel België • API HEAT' : '24/7 Emergency Service Across Belgium'}</span>
              </div>

              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.15]">
                {t.heroTitle}
              </h1>

              <p className="text-lg text-blue-100/90 leading-relaxed max-w-2xl">
                {t.heroSub}
              </p>

              {/* Specific Services Highlight Badges in Hero */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                <div className="bg-white/10 backdrop-blur-md border border-white/15 p-3 rounded-2xl flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-600/50 flex items-center justify-center text-white shrink-0">
                    <Wrench className="w-5 h-5 text-yellow-300" />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-white block">{t.heroBadge1Title}</span>
                    <span className="text-[11px] text-blue-200">{t.heroBadge1Sub}</span>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-md border border-white/15 p-3 rounded-2xl flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-600/50 flex items-center justify-center text-white shrink-0">
                    <Flame className="w-5 h-5 text-yellow-300" />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-white block">{t.heroBadge2Title}</span>
                    <span className="text-[11px] text-blue-200">{t.heroBadge2Sub}</span>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-md border border-white/15 p-3 rounded-2xl flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-600/50 flex items-center justify-center text-white shrink-0">
                    <ShieldAlert className="w-5 h-5 text-yellow-300" />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-white block">{t.heroBadge3Title}</span>
                    <span className="text-[11px] text-blue-200">{t.heroBadge3Sub}</span>
                  </div>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  onClick={() => setView('contact')}
                  aria-label="Demander une estimation gratuite en ligne"
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-gray-950 px-8 py-4 rounded-xl font-extrabold text-base shadow-xl shadow-amber-500/20 transition transform hover:-translate-y-0.5 active:translate-y-0 min-h-[48px]"
                >
                  <span>{t.ctaEstimate}</span>
                  <ArrowRight className="w-5 h-5" />
                </button>

                <a
                  href={`tel:${siteConfig.phone.replace(/[^0-9+]/g, '')}`}
                  aria-label={`Appeler le service d'urgence API HEAT au ${siteConfig.phone}`}
                  className="flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-bold text-base shadow-xl shadow-red-600/30 transition transform hover:-translate-y-0.5 min-h-[48px]"
                >
                  <Phone className="w-5 h-5 text-yellow-300 animate-bounce" />
                  <span>{t.ctaCallEmergency}: {siteConfig.phone}</span>
                </a>
              </div>

              {/* Trust Badges Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-8 border-t border-blue-800/60 text-xs text-blue-200">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-yellow-300 shrink-0" />
                  <span>30 min chrono</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-yellow-300 shrink-0" />
                  <span>Techniciens Agréés</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-yellow-300 shrink-0" />
                  <span>Garantie Qualité</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-300 shrink-0" />
                  <span>Avis 4.9/5 (4500+)</span>
                </div>
              </div>
            </div>

            {/* Right Card / Quick Estimate Box */}
            <div className="lg:col-span-5">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 p-8 rounded-3xl shadow-2xl space-y-6 text-gray-900">
                <div className="bg-white p-6 rounded-2xl shadow-md space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-xs font-black uppercase tracking-wider text-blue-950 bg-blue-100 border border-blue-300 px-2.5 py-1 rounded-md inline-block">
                        {currentLang === 'fr' ? 'Dépannage Rapide' : currentLang === 'nl' ? 'Snelle Bijstand' : 'Fast Response'}
                      </span>
                      <h2 className="text-xl font-bold text-gray-900 mt-2">
                        {currentLang === 'fr' ? 'Besoin d\'un dépannage urgent ?' : currentLang === 'nl' ? 'Dringende herstelling nodig?' : 'Need urgent repair?'}
                      </h2>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 shrink-0">
                      <ShieldAlert className="w-6 h-6 animate-pulse" />
                    </div>
                  </div>

                  <p className="text-sm text-gray-700">
                    {currentLang === 'fr' ? 'Fuite d\'eau, chaudière en panne, canalisation engorgée à Bruxelles, Anvers, Liège ou partout ailleurs en Belgique ?' : currentLang === 'nl' ? 'Waterlek, defecte ketel of verstopte afvoer in Brussel, Antwerpen, Luik of elders in België?' : 'Water leak, boiler breakdown, clogged drain in Brussels, Antwerp, Liège, or anywhere in Belgium?'}
                  </p>

                  <div className="space-y-2 pt-2 border-t border-gray-200 text-sm">
                    <div className="flex items-center justify-between text-gray-800">
                      <span>⏱️ {currentLang === 'fr' ? 'Temps d\'arrivée moyen' : currentLang === 'nl' ? 'Gemiddelde aankomsttijd' : 'Avg arrival time'}:</span>
                      <span className="font-extrabold text-blue-900">30 minutes</span>
                    </div>
                    <div className="flex items-center justify-between text-gray-800">
                      <span>💶 {currentLang === 'fr' ? 'Tarif transparent dès' : currentLang === 'nl' ? 'Transparant tarief vanaf' : 'Transparent rate from'}:</span>
                      <span className="font-black text-emerald-900 bg-emerald-100 px-2 py-0.5 rounded border border-emerald-300">85 € TVAC</span>
                    </div>
                    <div className="flex items-center justify-between text-gray-800">
                      <span>🇧🇪 {currentLang === 'fr' ? 'Couverture' : currentLang === 'nl' ? 'Dekking' : 'Coverage'}:</span>
                      <span className="font-extrabold text-gray-900">100% Belgique</span>
                    </div>
                  </div>

                  <button
                    onClick={() => setView('contact')}
                    className="w-full bg-blue-700 hover:bg-blue-800 text-white font-bold py-3.5 px-4 rounded-xl transition shadow-lg shadow-blue-700/25 flex items-center justify-center gap-2"
                  >
                    <span>{currentLang === 'fr' ? 'Planifier une intervention' : currentLang === 'nl' ? 'Interventie inplannen' : 'Schedule Intervention'}</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Local Proximity Trust Banner (Sint-Pieters-Leeuw & Brussels Area) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 relative z-30">
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xl p-4 sm:p-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-orange-700 shrink-0">
              <MapPin className="w-6 h-6 animate-bounce" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-black uppercase tracking-wider text-orange-950 bg-orange-100 border border-orange-300 px-2.5 py-0.5 rounded-md">
                  {currentLang === 'fr' ? 'Base Locale 24/7' : currentLang === 'nl' ? 'Lokale Uitvalsbasis 24/7' : 'Local 24/7 Hub'}
                </span>
                <span className="text-xs font-bold text-slate-700">Sint-Pieters-Leeuw (1600)</span>
              </div>
              <h2 className="text-base sm:text-lg font-extrabold text-slate-900 mt-1">
                {currentLang === 'fr' 
                  ? 'Intervention en 15 à 30 min chrono : Sint-Pieters-Leeuw, Halle, Anderlecht, Forest, Uccle & les 19 communes de Bruxelles'
                  : currentLang === 'nl'
                  ? 'Interventie binnen 15 tot 30 min: Sint-Pieters-Leeuw, Halle, Anderlecht, Vorst, Ukkel en de 19 Brusselse gemeenten'
                  : 'Fast 15-30 min arrival: Sint-Pieters-Leeuw, Halle, Anderlecht, Forest, Uccle & all 19 Brussels municipalities'}
              </h2>
              <p className="text-xs text-slate-600 mt-0.5">
                {currentLang === 'fr'
                  ? 'Camions d’intervention équipés en circulation permanente dans le Brabant Flamand, Bruxelles et toute la Belgique.'
                  : currentLang === 'nl'
                  ? 'Volledig uitgeruste interventiewagens permanent onderweg in Vlaams-Brabant, Brussel en heel België.'
                  : 'Fully equipped mobile units on call throughout Flemish Brabant, Brussels, and nationwide.'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0 w-full md:w-auto">
            <button
              onClick={() => setView('zones')}
              className="flex-1 md:flex-initial inline-flex items-center justify-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs sm:text-sm font-bold px-4 py-2.5 rounded-xl transition"
            >
              <span>{currentLang === 'fr' ? 'Vérifier ma commune' : currentLang === 'nl' ? 'Gemeente controleren' : 'Check municipality'}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <a
              href="tel:+32493373789"
              className="flex-1 md:flex-initial inline-flex items-center justify-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs sm:text-sm font-bold px-4 py-2.5 rounded-xl shadow-md transition"
            >
              <Phone className="w-3.5 h-3.5" />
              <span>0493 37 37 89</span>
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
            {t.featuresTitle}
          </h2>
          <p className="text-lg text-gray-600">
            {currentLang === 'fr' ? 'API HEAT s’engage sur la qualité, la rapidité et la transparence tarifaire pour tous vos travaux techniques.' : currentLang === 'nl' ? 'API HEAT garandeert kwaliteit, snelheid en transparante prijzen voor al uw technisch werk.' : 'API HEAT commits to quality, speed, and transparent pricing for all technical services.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-gray-100 hover:shadow-2xl transition-all group">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-6 group-hover:bg-blue-600 group-hover:text-white transition-colors">
              <Clock className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t.feat1Title}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{t.feat1Desc}</p>
          </div>

          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-gray-100 hover:shadow-2xl transition-all group">
            <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
              <Award className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t.feat2Title}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{t.feat2Desc}</p>
          </div>

          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-gray-100 hover:shadow-2xl transition-all group">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-6 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t.feat3Title}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{t.feat3Desc}</p>
          </div>

          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-gray-100 hover:shadow-2xl transition-all group">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mb-6 group-hover:bg-amber-600 group-hover:text-white transition-colors">
              <Star className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t.feat4Title}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{t.feat4Desc}</p>
          </div>
        </div>
      </section>

      {/* Visual Photo Showcase Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-slate-900 min-h-[380px] flex items-center">
          <div className="absolute inset-0 opacity-40">
            <img
              src={siteConfig.pageImages?.equipmentBanner || "https://images.unsplash.com/photo-1581092160607-ee22621dd758?q=70&w=800&auto=format&fit=crop"}
              alt={getEquipmentBannerImageSeo(currentLang).alt}
              title={getEquipmentBannerImageSeo(currentLang).title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent"></div>
          <div className="relative z-10 p-8 sm:p-12 lg:p-16 max-w-2xl space-y-6 text-white">
            <span className="bg-orange-500/20 text-orange-400 border border-orange-500/30 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
              {currentLang === 'fr' ? 'Équipement Professionnel Certifié' : currentLang === 'nl' ? 'Gecertificeerde Uitrusting' : 'Certified Professional Equipment'}
            </span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight">
              {currentLang === 'fr' ? 'Interventions de pointe à Bruxelles, en Flandre et en Wallonie' : currentLang === 'nl' ? 'Geavanceerde interventies in Brussel, Vlaanderen en Wallonië' : 'Advanced Interventions Across Belgium'}
            </h2>
            <p className="text-slate-300 text-base leading-relaxed">
              {currentLang === 'fr'
                ? 'Nos techniciens disposent de caméras thermiques, d’appareils de recherche de fuite non destructive et de stations d’hydrocurage haute pression pour résoudre vos problèmes sans dégâts.'
                : currentLang === 'nl'
                ? 'Onze technici zijn uitgerust met thermische camera\'s, niet-destructieve lekdetectieapparatuur en hogedrukreinigers om uw problemen vakkundig op te lossen.'
                : 'Our technicians are equipped with thermal cameras, non-destructive leak detectors, and high-pressure jetting units to solve your issues without damage.'}
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <button
                onClick={() => setView('zones')}
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3.5 rounded-xl font-bold transition shadow-lg"
              >
                <MapPin className="w-4 h-4" />
                <span>{currentLang === 'fr' ? 'Voir nos zones d\'intervention' : currentLang === 'nl' ? 'Bekijk onze interventiezones' : 'View our coverage zones'}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="bg-gray-50 py-20 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div className="space-y-3">
              <span className="text-xs font-black uppercase tracking-wider text-blue-950 bg-blue-100 border border-blue-300 px-3 py-1 rounded-full inline-block">
                {currentLang === 'fr' ? 'Expertise Métier' : currentLang === 'nl' ? 'Vakkennis' : 'Our Expertise'}
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
                {t.servicesSectionTitle}
              </h2>
              <p className="text-gray-600 max-w-2xl text-base">
                {t.servicesSectionSub}
              </p>
            </div>
            <button
              onClick={() => setView('services')}
              className="inline-flex items-center gap-2 text-blue-600 font-bold hover:text-blue-700 transition"
            >
              <span>{currentLang === 'fr' ? 'Voir tous les services' : currentLang === 'nl' ? 'Alle diensten bekijken' : 'View all services'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {SERVICES.map((s) => (
              <div key={s.id} className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-lg hover:shadow-xl transition flex flex-col justify-between group">
                <div className="relative h-52 overflow-hidden bg-slate-100">
                  <img
                    src={serviceImages[s.id]}
                    alt={getServiceImageSeo(s.id, currentLang).alt}
                    title={getServiceImageSeo(s.id, currentLang).title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    referrerPolicy="no-referrer"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                  {s.fixedPrices?.[currentLang] && s.fixedPrices[currentLang].length > 0 ? (
                    <div className="absolute top-3.5 right-3.5 bg-orange-700 text-white text-xs font-black px-3 py-1.5 rounded-full shadow-lg border border-orange-500/60 flex items-center gap-1">
                      <span className="text-[10px] uppercase font-bold text-white">{currentLang === 'fr' ? 'Dès' : currentLang === 'nl' ? 'Vanaf' : 'From'}</span>
                      <span className="font-black text-white">{s.fixedPrices[currentLang][0].price}</span>
                    </div>
                  ) : s.priceStartingAt ? (
                    <div className="absolute top-3.5 right-3.5 bg-orange-700 text-white text-xs font-black px-3 py-1.5 rounded-full shadow-lg border border-orange-500/60 flex items-center gap-1">
                      <span className="text-[10px] uppercase font-bold text-white">{currentLang === 'fr' ? 'Tarif :' : currentLang === 'nl' ? 'Tarief:' : 'Rate:'}</span>
                      <span className="font-black text-white">{typeof s.priceStartingAt === 'string' ? s.priceStartingAt : s.priceStartingAt[currentLang]}</span>
                    </div>
                  ) : null}
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <span className="font-bold text-lg drop-shadow">{s.title[currentLang]}</span>
                  </div>
                </div>

                <div className="p-8 space-y-6">
                  <p className="text-gray-700 text-sm leading-relaxed">{s.shortDesc[currentLang]}</p>

                  <ul className="space-y-2.5 pt-4 border-t border-gray-100 text-sm text-gray-800">
                    {s.features[currentLang].slice(0, 3).map((feat, idx) => (
                      <li key={idx} className="flex items-start gap-2.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="px-8 py-5 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-700">
                    {currentLang === 'fr' ? 'Agréé Belgique' : currentLang === 'nl' ? 'Erkend België' : 'Belgium Certified'}
                  </span>
                  <button
                    onClick={() => setView(`service-${s.id}`)}
                    aria-label={`En savoir plus sur le service ${s.title[currentLang]}`}
                    className="bg-orange-700 hover:bg-orange-800 text-white px-5 py-2.5 rounded-xl font-bold text-sm transition shadow min-h-[44px]"
                  >
                    {t.details}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto space-y-4 mb-16">
          <span className="text-xs font-black uppercase tracking-wider text-emerald-950 bg-emerald-100 border border-emerald-300 px-3 py-1 rounded-full inline-block">
            {currentLang === 'fr' ? 'Avis Vérifiés' : currentLang === 'nl' ? 'Geverifieerde Beoordelingen' : 'Verified Reviews'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
            {t.testimonialsTitle}
          </h2>
          <p className="text-gray-700 text-base">{t.testimonialsSub}</p>
        </div>

        <TestimonialsCarousel currentLang={currentLang} />
      </section>

      {/* Frequently Asked Questions (FAQ) with Schema.org optimization */}
      <FaqSection currentLang={currentLang} onNavigateContact={() => setView('contact')} />

      {/* Maillage Interne : Communes Prioritaires desservies 24/7 */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Suspense fallback={<div className="py-8 text-center text-slate-500">Chargement des communes desservies...</div>}>
          <InternalLinkingSection
            currentLang={currentLang}
            onNavigate={setView}
            showCities={true}
            showServices={false}
          />
        </Suspense>
      </section>

      {/* Call to Action Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800 rounded-3xl p-8 sm:p-12 text-white shadow-2xl flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="space-y-3 text-center lg:text-left">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
              {currentLang === 'fr' ? 'Besoin d\'un dépannage urgent ou d\'un devis ?' : currentLang === 'nl' ? 'Dringende herstelling of offerte nodig?' : 'Need urgent repair or a quote?'}
            </h2>
            <p className="text-blue-100 max-w-xl text-sm sm:text-base">
              {currentLang === 'fr' ? 'Nos experts plombiers et chauffagistes interviennent rapidement dans toutes les communes de Belgique.' : currentLang === 'nl' ? 'Onze experten loodgieters en verwarmingstechnici staan snel paraat in heel België.' : 'Our expert plumbers and heating engineers respond rapidly across all Belgian municipalities.'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 shrink-0">
            <a
              href="tel:+32493373789"
              aria-label="Appeler API HEAT d'urgence au 0493 37 37 89"
              className="flex items-center justify-center gap-3 bg-white text-blue-900 hover:bg-blue-50 px-8 py-4 rounded-xl font-black text-base shadow-lg transition min-h-[48px]"
            >
              <Phone className="w-5 h-5 text-red-600" aria-hidden="true" />
              <span>0493 37 37 89</span>
            </a>
            <button
              onClick={() => setView('contact')}
              aria-label="Demander un devis gratuit en ligne"
              className="flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-gray-950 px-8 py-4 rounded-xl font-extrabold text-base shadow-lg transition min-h-[48px]"
            >
              <span>{t.ctaEstimate}</span>
              <ArrowRight className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
